// src/DivisionSelect.js
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api";

export default function DivisionSelect({ onSelect }) {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMsg, setStatusMsg] = useState("");
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;

    async function loadOnce(label) {
      const res = await apiFetch("/api/divisions");
      const text = await res.text().catch(() => "");
      console.log(`[DIVISIONS ${label}]`, res.status, text.slice(0, 200));
      let data = [];
      try {
        data = text ? JSON.parse(text) : [];
      } catch {
        data = [];
      }
      return { res, data };
    }

    async function load() {
      setLoading(true);
      setErr("");
      setStatusMsg("");

      const delays = [1000, 2000, 3000, 5000];
      try {
        for (let i = 0; i <= delays.length; i++) {
          const label = i === 0 ? "1st" : `retry-${i}`;
          const { res, data } = await loadOnce(label);

          if (res.ok && Array.isArray(data) && data.length > 0) {
            if (alive) {
              setDivisions(data);
              setStatusMsg("");
              setErr("");
            }
            return;
          }

          if (alive) setStatusMsg("Waking up server… retrying…");
          if (i < delays.length) await new Promise((r) => setTimeout(r, delays[i]));
        }

        if (alive) {
          setStatusMsg("");
          setErr("Failed to load divisions");
        }
      } catch (e) {
        console.error(e);
        if (alive) {
          setStatusMsg("");
          setErr("Failed to load divisions");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const needle = (q || "").trim().toLowerCase();
    if (!needle) return divisions;
    return (divisions || []).filter((d) => String(d).toLowerCase().includes(needle));
  }, [divisions, q]);

  const card = {
    maxWidth: 560,
    margin: "40px auto",
    padding: 18,
  };

  const panel = {
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
    padding: 18,
  };

  const btn = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    cursor: "pointer",
    textAlign: "left",
    fontWeight: 600,
  };

  const subtle = { color: "#6b7280", fontSize: 13 };

  return (
    <div style={card}>
      <div style={panel}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>Select Division</div>
            <div style={subtle}>Choose where you’re working today.</div>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search divisions…"
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              outline: "none",
            }}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          {loading && <div style={subtle}>Loading divisions…</div>}
          {!loading && statusMsg && <div style={subtle}>{statusMsg}</div>}
          {err && <div style={{ marginTop: 8, color: "#b91c1c", fontWeight: 600 }}>{err}</div>}

          {!loading && !err && filtered.length === 0 && (
            <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 600 }}>
              No divisions found.
            </div>
          )}
        </div>

        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          {filtered.map((d) => (
            <button
              key={d}
              onClick={() => onSelect(d)}
              style={btn}
              type="button"
              onMouseEnter={(e) => (e.currentTarget.style.background = "#eef2ff")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#f9fafb")}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>🏷️</span>
                <div style={{ flex: 1 }}>
                  <div>{d}</div>
                  <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
                    Tap to continue
                  </div>
                </div>
                <span style={{ color: "#9ca3af" }}>→</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
